import { ReactNode } from "react";
import {
  TArrayToPronounHandledGenders,
  TArrayToSentenceConfig,
} from "./types/string-utils.types";

/** Joins the given array of words,
 * using the lastSeparator to link the last word with the rest and the
 * separator to join all other words.
 * Here are some examples using the default separator values.
 * Given the array ["w","x","y","z"] returns the string "w, x, y and z".
 * Given the array ["x","y"] returns the string "x and y".
 * Given the array ["x"] returns "x".
 * Given the array [] returns "".
 * @param inWords The words that should be turned into a sentence.
 * @param [lastSeparator=" and "] Can be used to override the separator that will be used to
 * join the last element of the array with the rest of the sentence.
 * @param [separator=", "] Can be used to override the separator that will be used to join the parts in the array.
 * @param [defaultValue=""] If given an empty array, this value will be returned.
 * @param [capitaliseFirstLetter=false] If set to true, each word will be trimmed and have its first letter capitalised.
 * We don't lowercase the rest of the word by default, because someone might have a cat called "Paris & Kiara", in which
 * case lowercasing it would result into "Paris & kiara", which is probably not what we want.
 * We'd need to implement a more complex capitalisation function to handle these sorts of cases.
 * */
export function arrayToSentence(
  inWords: string[],
  {
    lastSeparator = " and ",
    separator = ", ",
    defaultValue = "",
    capitaliseFirstLetter = false,
  }: TArrayToSentenceConfig = {},
): string {
  let words = inWords;
  if (words.length < 1) {
    return defaultValue;
  }
  if (capitaliseFirstLetter) {
    words = words.map((word) => capitaliseFirst(word));
  }
  if (words.length === 1) {
    return words[0];
  }
  const lastWord = words.at(-1);
  const theRest = words.slice(0, -1);

  return [ theRest.join(separator), lastWord ].join(lastSeparator);
}

/** Converts an array of gendered inputs to a pronoun string based on the provided gender mapping.
 * @param inputWithGender - The array of input elements with associated genders.
 * @param boy - The pronoun to return for male or boy gender.
 * @param girl - The pronoun to return for female or girl gender.
 * @param neutral - The pronoun to return for unknown or non-singular input.
 * @param genderGetter - A function that extracts the gender from an input element.
 * @return The corresponding pronoun for the determined gender,
 * or the neutral pronoun if the array length is not one or gender is unknown.
 */
export function arrayToPronoun<
  GENDER extends TArrayToPronounHandledGenders,
  BOY extends string,
  GIRL extends string,
  NEUTRAL extends string,
  INPUT,
>(
  inputWithGender: INPUT[],
  boy: BOY,
  girl: GIRL,
  neutral: NEUTRAL,
  genderGetter: (getFrom: INPUT) => GENDER,
): BOY | GIRL | NEUTRAL {
  if (inputWithGender.length !== 1) {
    return neutral;
  }

  const singleInput = inputWithGender[0];
  const gender = genderGetter(singleInput);

  switch (gender) {
    case "MALE":
    case "BOY":
      return boy;
    case "FEMALE":
    case "GIRL":
      return girl;
    case "UNKNOWN":
    default:
      return neutral;
  }
}

/** Capitalises the first character of a given string after trimming it.
 * Optionally, converts the rest of the string to lowercase.
 * @param s - The input string to process.
 * @param [lowercase=false] - Whether the rest of the string should be converted to lowercase.
 * @return The processed string with the first character capitalised.
 */
export function capitaliseFirst(
  s: string,
  lowercase = false,
): string {
  // Handle empty strings
  const trimmed = s.trim();
  if (!trimmed) {
    return trimmed;
  }

  // Use the spread operator to properly handle Unicode characters
  const chars = [ ...trimmed ];
  const first = chars[0];
  const rest = chars.slice(1).join("");

  return first.toLocaleUpperCase() + (lowercase ? rest.toLocaleLowerCase() : rest);
}

/**
 * Capitalises the first letter of each sentence in a string.
 * Sonar: Input is length-guarded and uses the preferred .exec method, so it is safe from ReDoS.
 */
export function capitaliseFirstInEachSentence(s: string): string {
  // Handle empty strings and limit input length
  if (!s || s.length > 1000) {
    return s;
  }

  // Preserve all leading spaces (do not trim)
  const leadingSpaces = s.slice(0, s.search(/\S|$/));
  const trimmed = s.trim();
  if (!trimmed) {
    return leadingSpaces;
  }

  // Split the string into sentences using a safe split
  const sentences = trimmed.split(/([.!?]+\s*)/g).reduce<string[]>((acc, part, idx, arr) => {
    if (idx % 2 === 0) {
      // Sentence body
      const punctuation = arr[idx + 1] || "";
      if (part || punctuation) {
        acc.push(part + punctuation);
      }
    }
    return acc;
  }, []);

  // Capitalise first letter of each using existing util
  const capitalisedSentences = sentences.map((sentence) => {
    // Use RegExp.exec() for Sonar compliance and safety
    const leadingMatch = /^(\s*)/.exec(sentence);
    const leading = leadingMatch ? leadingMatch[1] : "";
    const firstChar = sentence.slice(leading.length, leading.length + 1);
    const rest = sentence.slice(leading.length + 1);
    if (firstChar) {
      return leading + firstChar.toUpperCase() + rest;
    }
    return capitaliseFirst(sentence);
  });

  // Join sentences back into a single string with original leading spaces
  return leadingSpaces + capitalisedSentences.join("");
}


export const isTextString = (text: string | ReactNode): text is string => {
  return typeof text === "string";
};